<script lang="ts">
  import UnifiedCheckButton from './UnifiedCheckButton.svelte'
  
  interface Props {
    editorText: string
    isChecking: boolean
    hasChecked: boolean
    hasErrors: boolean
    canCheck: boolean
    errorCount?: number
    totalErrorsIncludingKept?: number
    errors?: any[]
    onCheck: () => void
    onAIAnalysis?: () => void
    onCancelCheck?: () => void
    isInSplitView?: boolean
    hasInlineViews?: boolean
    saveStatus?: 'idle' | 'saving' | 'saved' | 'error'
    lastSaved?: Date | null
    statusMessage?: string | null
  }

  let { 
    editorText, 
    isChecking, 
    hasChecked,
    hasErrors, 
    canCheck,
    errorCount = 0,
    totalErrorsIncludingKept = 0,
    errors = [],
    onCheck,
    onAIAnalysis,
    onCancelCheck,
    isInSplitView = false,
    hasInlineViews = false,
    saveStatus = 'idle',
    lastSaved = null,
    statusMessage = null
  }: Props = $props()

  let showTooltip = $state<string | null>(null)

  function showButtonTooltip(id: string) {
    showTooltip = id
  }

  function hideTooltip() {
    showTooltip = null
  }

  // Auto-hide save status after 3 seconds
  $effect(() => {
    if (saveStatus === 'saved' || saveStatus === 'error') {
      const timer = setTimeout(() => {
        // Status will be reset by parent component
      }, 3000)
      return () => clearTimeout(timer)
    }
  })

</script>

<footer class="editor-footer">
  <div class="footer-content">
    <!-- Left side - Status message -->
    <div class="footer-left">
      <div class="status-message">
        {#if saveStatus === 'saving'}
          <span class="status-saving">저장 중...</span>
        {:else if saveStatus === 'saved'}
          <span class="status-saved">저장됨</span>
        {:else if saveStatus === 'error'}
          <span class="status-error">저장 실패</span>
        {:else if isChecking}
          <span class="status-checking">맞춤법 검사 중...</span>
          {#if onCancelCheck}
            <button class="cancel-button" onclick={onCancelCheck}>
              취소
            </button>
          {/if}
        {:else if statusMessage && (statusMessage.includes('AI') || statusMessage.includes('분석'))}
          <span class="status-checking">{statusMessage}</span>
          {#if onCancelCheck}
            <button class="cancel-button" onclick={onCancelCheck}>
              취소
            </button>
          {/if}
        {:else if hasErrors}
          <span class="status-error">{errorCount}개의 오류가 발견되었습니다</span>
        {:else if hasChecked && totalErrorsIncludingKept > 0 && !hasErrors}
          <span class="status-success">모든 오류 처리가 완료되었습니다</span>
        {:else if hasChecked && !hasErrors}
          <span class="status-success">모든 오류가 수정되었습니다</span>
        {:else if saveStatus === 'saved' && lastSaved}
          <span class="status-saved">저장됨</span>
        {:else if saveStatus === 'saving'}
          <span class="status-saving">저장 중...</span>
        {:else}
          <span class="status-idle"></span>
        {/if}
      </div>
    </div>

    <!-- Right side - Unified check button -->
    <div class="footer-right">
      <div class="unified-check-wrapper">
        <UnifiedCheckButton
          {onCheck}
          {onAIAnalysis}
          disabled={!canCheck || isInSplitView || hasInlineViews}
          {isChecking}
          {editorText}
        />
      </div>
    </div>
  </div>
</footer>

<style>
  .editor-footer {
    background: var(--bg-secondary);
    border-top: 1px solid var(--border-primary);
    padding: 8px 16px;
    position: relative;
    z-index: 10;
    flex-shrink: 0;
    min-height: 41px;
    height: 41px;
    display: flex;
    align-items: center;
  }

  .footer-content {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    gap: 16px;
  }

  .footer-left {
    flex: 1;
    display: flex;
    align-items: center;
    min-width: 0; /* Allow text truncation */
  }

  .footer-right {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0; /* Prevent button from shrinking */
  }

  /* Status messages */
  .status-message {
    font-size: 12px;
    font-weight: 500;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .status-idle {
    color: var(--text-tertiary);
  }

  .status-saving {
    color: var(--info);
  }

  .status-saved {
    color: var(--success);
  }

  .status-checking {
    color: var(--info);
  }

  .status-error {
    color: var(--error);
  }

  .status-success {
    color: var(--success);
    font-weight: 500;
  }
  
  /* Cancel button */
  .cancel-button {
    margin-left: 8px;
    padding: 2px 8px;
    background: transparent;
    color: var(--text-secondary);
    border: 1px solid var(--border-secondary);
    border-radius: 3px;
    font-size: 11px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.15s ease;
  }
  
  .cancel-button:hover {
    background: var(--bg-hover);
    border-color: var(--border-primary);
    color: var(--text-primary);
  }
  
  .cancel-button:active {
    transform: scale(0.95);
  }

  
  /* Unified check wrapper */
  .unified-check-wrapper {
    display: flex;
    align-items: center;
    position: relative;
  }



  /* Responsive Design */
  @media (max-width: 768px) {
    .editor-footer {
      padding: 6px 12px;
    }


    .status-message {
      font-size: 10px;
    }
  }

  @media (max-width: 480px) {
    .footer-right {
      gap: 4px;
    }

  }
  
  /* Dark mode handled by CSS variables */

  /* Demo mode - ensure proper footer positioning */
  :global(.demo-mode) .editor-footer {
    position: relative;
    margin-bottom: 0;
  }
</style>